import React, { useState, useEffect, memo } from 'react';
import { ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { resolveFileUrl, resolveFileUrlSync } from '../../utils/resolveFileUrl';
import i18n from '../../i18n';

/**
 * Chat image with unified URL resolution and graceful error fallback.
 */
const ChatImage = memo(({
  fileUrl,
  alt = i18n.t('shared:chatImage.defaultAlt'),
  className = 'max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition-opacity',
  onClick,
}) => {
  const { t } = useTranslation('shared');
  const rawUrl = fileUrl;
  const [src, setSrc] = useState(() => resolveFileUrlSync(rawUrl) || rawUrl);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    let cancelled = false;

    (async () => {
      const resolved = await resolveFileUrl(rawUrl);
      if (!cancelled && resolved) {
        setSrc(resolved);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rawUrl]);

  if (!rawUrl) return null;

  if (failed) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#161f31] border border-white/[0.08] text-[#7a96b0] text-xs">
        <ImageOff className="w-4 h-4 flex-shrink-0" />
        <span>{t('chatImage.loadFailed')}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={() => setFailed(true)}
      loading="lazy"
    />
  );
});

ChatImage.displayName = 'ChatImage';
export default ChatImage;
